import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { LogOut, WifiOff, ShieldCheck } from 'lucide-react';
import { currentUser, isAdmin, onAuthChange, refreshAuth, signOut, type AuthUser } from './lib/pb';
import { useOnline } from './lib/hooks';
import SignIn from './components/SignIn';
import CampaignWorkspace from './components/CampaignWorkspace';
import VideoCombiner from './components/VideoCombiner';
import Statistics from './components/Statistics';
import RequestsAndSuggestions from './components/RequestsAndSuggestions';
import AdminPanel from './components/admin/AdminPanel';
import { ConfirmHost } from './components/ConfirmDialog';

type Tab = 'generator' | 'combiner' | 'stats' | 'requests' | 'admin';

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(currentUser());
  const [tab, setTab] = useState<Tab>('generator');
  const online = useOnline();

  useEffect(() => onAuthChange(setUser), []);

  // Validate the stored token once on load; a revoked or expired session drops
  // back to the sign-in screen instead of a workspace where every request 401s.
  useEffect(() => {
    void refreshAuth().then(() => setUser(currentUser()));
  }, []);

  const admin = useMemo(() => isAdmin(), [user]);

  // If an admin loses the role while sitting on the admin tab, don't strand them.
  useEffect(() => {
    if (tab === 'admin' && !admin) setTab('generator');
  }, [tab, admin]);

  const tabs: Array<{ id: Tab; label: string; adminOnly?: boolean }> = [
    { id: 'generator', label: 'Video Generator' },
    { id: 'combiner', label: 'Video Stitcher' },
    { id: 'stats', label: 'Statistics' },
    { id: 'requests', label: 'Requests & Suggestions' },
    { id: 'admin', label: 'Admin', adminOnly: true },
  ];

  return (
    <div className="min-h-screen font-sans bg-natural-bg text-natural-text">
      <ConfirmHost />
      <header className="sticky top-0 z-50 bg-white/60 backdrop-blur-md border-b border-natural-border px-4 md:px-8 py-3.5 flex items-center justify-between shadow-xs">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-natural-sage rounded-full flex items-center justify-center text-lg shrink-0 shadow-xs">
            🙇
          </div>
          <h1 className="font-serif text-lg md:text-xl font-medium tracking-tight leading-tight">
            AI Endorser <span className="text-natural-sage font-light">Content Factory</span>
          </h1>
        </div>

        <div className="flex items-center gap-3">
          {!online && (
            <span
              className="zen-pill bg-amber-50 text-amber-700"
              title="Live updates are paused until the connection returns"
            >
              <WifiOff size={11} />
              Offline
            </span>
          )}

          {user && (
            <div className="flex items-center gap-2 border-l border-natural-border pl-3">
              {admin && (
                <span className="zen-pill bg-natural-sage/15 text-natural-sage hidden sm:inline-flex">
                  <ShieldCheck size={11} />
                  Admin
                </span>
              )}
              <span className="text-xs text-natural-text/60 hidden md:inline font-mono">
                {user.email}
              </span>
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="w-7 h-7 rounded-full object-cover border border-natural-border shrink-0"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-natural-sage/20 flex items-center justify-center text-xs text-natural-sage font-bold font-serif border border-natural-border shrink-0">
                  {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                </div>
              )}
              <button
                onClick={signOut}
                className="p-1.5 rounded-xl text-natural-text/50 hover:text-red-500 hover:bg-natural-border/20 transition-colors cursor-pointer"
                title="Sign out"
              >
                <LogOut size={14} />
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <AnimatePresence mode="wait">
          {!user ? (
            <SignIn key="signin" onSignedIn={() => setUser(currentUser())} />
          ) : (
            <motion.div key="workspace" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
              <div className="flex border-b border-natural-border/60 pb-px overflow-x-auto">
                {tabs
                  .filter((t) => !t.adminOnly || admin)
                  .map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      className={`zen-tab whitespace-nowrap ${tab === t.id ? 'zen-tab-active' : ''}`}
                    >
                      {t.label}
                    </button>
                  ))}
              </div>

              {tab === 'generator' && <CampaignWorkspace user={user} />}
              {tab === 'combiner' && <VideoCombiner />}
              {tab === 'stats' && <Statistics />}
              {tab === 'requests' && <RequestsAndSuggestions user={user} isAdmin={admin} />}
              {tab === 'admin' && admin && <AdminPanel />}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
